import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import type { ApiCheckerSettingName } from "api/ApiKeySettings";
import type { useApiCheckerSettings } from "hooks/api/use-api-checker-settings";
import {
  BL_MODE_DETAILED,
  BL_MODE_FAST,
  CUSTOM_CHECK_PROVIDERS,
  IPQS_STRICTNESS_LEVELS,
  REPORT_MODE_AUTOMATED,
  REPORT_MODE_CUSTOM,
  isAutomatedMode,
  isOn,
} from "utils/apiChecker/settings";
import { radioSx, switchSx } from "utils/chackra/controlStyles";
import Panel from "./Panel";

type SettingsState = ReturnType<typeof useApiCheckerSettings>;

const Legend = ({ id, children }: { id: string; children: string }) => (
  <Text
    as="legend"
    id={id}
    fontSize="md"
    fontWeight="semibold"
    color="text.primary"
    mb={2}
  >
    {children}
  </Text>
);

/**
 * The key's checker settings, the same ones the Telegram bot's API menu edits
 * (spec 2026-10-20-website-api-settings, US2). One request per control,
 * optimistic, rolled back with an error on any failed save.
 */
const ApiSettingsPanel = ({ state }: { state: SettingsState }) => {
  const intl = useIntl();
  const toast = useToast();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);
  const { settings, loading, loadError, pending, update } = state;

  const save = async (checker: ApiCheckerSettingName, value: number) => {
    const ok = await update(checker, value);
    toast({
      position: "top",
      status: ok ? "success" : "error",
      title: t(ok ? "profileApi.settings.saved" : "profileApi.settings.saveError"),
      duration: ok ? 2000 : 5000,
      isClosable: true,
    });
  };

  let body: JSX.Element;
  if (loadError) {
    body = (
      <Alert
        status="error"
        variant="left-accent"
        bg="status.errorSubtle"
        borderColor="status.error"
        color="text.primary"
        borderRadius="md"
        data-testid="api-settings-load-error"
      >
        <AlertIcon color="status.error" />
        <AlertDescription fontSize="sm">
          {t("profileApi.settings.loadError")}
        </AlertDescription>
      </Alert>
    );
  } else if (loading || !settings) {
    body = (
      <Text role="status" color="text.secondary">
        {t("profileApi.loading")}
      </Text>
    );
  } else {
    const automated = isAutomatedMode(settings.mode);
    const strictness = Number(settings.ipqs_strictness) || 0;
    const blMode = isOn(settings.bl_mode) ? BL_MODE_DETAILED : BL_MODE_FAST;
    body = (
      <Stack spacing={6}>
        <Box as="fieldset">
          <Legend id="api-mode-legend">{t("profileApi.settings.mode.legend")}</Legend>
          <RadioGroup
            value={String(automated ? REPORT_MODE_AUTOMATED : REPORT_MODE_CUSTOM)}
            onChange={(value) => save("mode", Number(value))}
            isDisabled={Boolean(pending.mode)}
            aria-labelledby="api-mode-legend"
          >
            <Stack spacing={3}>
              {[
                { value: REPORT_MODE_AUTOMATED, key: "automated" },
                { value: REPORT_MODE_CUSTOM, key: "custom" },
              ].map(({ value, key }) => (
                <Radio
                  key={key}
                  value={String(value)}
                  sx={radioSx}
                  alignItems="flex-start"
                  aria-describedby={`api-mode-${key}-hint`}
                >
                  <Text color="text.primary" fontWeight="medium">
                    {t(`profileApi.settings.mode.${key}`)}
                  </Text>
                  <Text
                    id={`api-mode-${key}-hint`}
                    fontSize="sm"
                    color="text.secondary"
                  >
                    {t(`profileApi.settings.mode.${key}Hint`)}
                  </Text>
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </Box>

        {!automated && (
          <Box as="fieldset" data-testid="api-provider-toggles">
            <Legend id="api-providers-legend">
              {t("profileApi.settings.providers.legend")}
            </Legend>
            <Stack spacing={3}>
              {CUSTOM_CHECK_PROVIDERS.map(({ name, label, paid }) => (
                <FormControl key={name} isDisabled={Boolean(pending[name])}>
                  <Flex align="center" justify="space-between" gap={4}>
                    <FormLabel htmlFor={`api-provider-${name}`} mb={0} mr={0}>
                      <Text as="span" display="block" color="text.primary">
                        {label}
                      </Text>
                      <Text as="span" display="block" fontSize="sm" color="text.secondary">
                        {t(
                          paid
                            ? "profileApi.settings.provider.paid"
                            : "profileApi.settings.provider.free"
                        )}
                      </Text>
                    </FormLabel>
                    <Switch
                      id={`api-provider-${name}`}
                      isChecked={isOn(settings[name])}
                      onChange={(e) => save(name, e.target.checked ? 1 : 0)}
                      sx={switchSx}
                    />
                  </Flex>
                </FormControl>
              ))}
            </Stack>
          </Box>
        )}

        <Box as="fieldset">
          <Legend id="api-ipqs-legend">{t("profileApi.settings.ipqs.legend")}</Legend>
          <Text fontSize="sm" color="text.secondary" mb={2} id="api-ipqs-hint">
            {t("profileApi.settings.ipqs.hint")}
          </Text>
          <RadioGroup
            value={String(strictness)}
            onChange={(value) => save("ipqs_strictness", Number(value))}
            isDisabled={Boolean(pending.ipqs_strictness)}
            aria-labelledby="api-ipqs-legend"
            aria-describedby="api-ipqs-hint"
          >
            <Flex gap={6} wrap="wrap">
              {IPQS_STRICTNESS_LEVELS.map((level) => (
                <Radio key={level} value={String(level)} sx={radioSx}>
                  <Text color="text.primary">
                    {t("profileApi.settings.ipqs.level", { level })}
                  </Text>
                </Radio>
              ))}
            </Flex>
          </RadioGroup>
        </Box>

        <Box as="fieldset">
          <Legend id="api-bl-legend">{t("profileApi.settings.bl.legend")}</Legend>
          <RadioGroup
            value={String(blMode)}
            onChange={(value) => save("bl_mode", Number(value))}
            isDisabled={Boolean(pending.bl_mode)}
            aria-labelledby="api-bl-legend"
          >
            <Flex gap={6} wrap="wrap">
              <Radio value={String(BL_MODE_FAST)} sx={radioSx}>
                <Text color="text.primary">{t("profileApi.settings.bl.fast")}</Text>
              </Radio>
              <Radio value={String(BL_MODE_DETAILED)} sx={radioSx}>
                <Text color="text.primary">{t("profileApi.settings.bl.detailed")}</Text>
              </Radio>
            </Flex>
          </RadioGroup>
        </Box>
      </Stack>
    );
  }

  return (
    <Panel id="api-settings" title={t("profileApi.settings.heading")}>
      <Text fontSize="sm" color="text.secondary" mb={4}>
        {t("profileApi.settings.note")}
      </Text>
      {body}
    </Panel>
  );
};

export default ApiSettingsPanel;
